import type { Operation } from '@safe-global/safe-gateway-typescript-sdk'
import {
  ConflictType,
  DetailedExecutionInfoType,
  LabelValue,
  TransactionInfoType,
  TransactionListItemType,
  TransactionStatus,
  TransactionTokenType,
  TransferDirection,
  type TransactionDetails,
  type TransactionListPage,
} from '@safe-global/safe-gateway-typescript-sdk'
import type { SafeTransaction } from '@safe-global/safe-core-sdk-types'
import { ZERO_ADDRESS } from '@safe-global/protocol-kit/dist/src/utils/constants'
import { getAddress } from 'ethers'
import local from '@/services/local-storage/local'
import { checksumAddress, sameAddress } from '@/utils/addresses'
import { fetchSafeInfo } from '@/services/safe-info'

const STORAGE_KEY = 'appchainTxQueue'

type StoredTx = {
  chainId: string
  details: TransactionDetails
}

type StoredQueue = Record<string, StoredTx>

const getTxId = (safeAddress: string, safeTxHash: string): string => {
  return `multisig_${getAddress(safeAddress)}_${safeTxHash}`
}

const toAddressEx = (value: string) => ({ value: checksumAddress(value) })

const readQueue = (): StoredQueue => {
  return local.getItem<StoredQueue>(STORAGE_KEY) ?? {}
}

const writeQueue = (queue: StoredQueue): void => {
  local.setItem(STORAGE_KEY, queue)
}

const getConfirmations = (details: TransactionDetails) => {
  if (details.detailedExecutionInfo && details.detailedExecutionInfo.type === DetailedExecutionInfoType.MULTISIG) {
    return details.detailedExecutionInfo.confirmations
  }
  return []
}

export const getLocalProposedTx = (txId: string): TransactionDetails | undefined => {
  return readQueue()[txId]?.details
}

export const removeLocalProposedTx = (txId: string): void => {
  const queue = readQueue()
  if (!queue[txId]) return
  delete queue[txId]
  writeQueue(queue)
}

export const listLocalProposedTxs = (chainId: string, safeAddress: string): TransactionDetails[] => {
  return Object.values(readQueue())
    .filter((item) => item.chainId === chainId && sameAddress(item.details.safeAddress, safeAddress))
    .map((item) => item.details)
    .filter((details) => getConfirmations(details).length > 0)
    .sort((a, b) => {
      const aNonce = a.detailedExecutionInfo && 'nonce' in a.detailedExecutionInfo ? a.detailedExecutionInfo.nonce : 0
      const bNonce = b.detailedExecutionInfo && 'nonce' in b.detailedExecutionInfo ? b.detailedExecutionInfo.nonce : 0
      return aNonce - bNonce
    })
}

export const localQueueToPage = (chainId: string, safeAddress: string): TransactionListPage => {
  const txs = listLocalProposedTxs(chainId, safeAddress)
  if (txs.length === 0) {
    return { results: [] }
  }

  return {
    results: [
      { type: TransactionListItemType.LABEL, label: LabelValue.Next },
      ...txs.map((details) => {
        const detailedExecutionInfo = details.detailedExecutionInfo
        const executionInfo =
          detailedExecutionInfo && detailedExecutionInfo.type === DetailedExecutionInfoType.MULTISIG
            ? {
                type: DetailedExecutionInfoType.MULTISIG as const,
                nonce: detailedExecutionInfo.nonce,
                confirmationsRequired: detailedExecutionInfo.confirmationsRequired,
                confirmationsSubmitted: detailedExecutionInfo.confirmations.length,
                missingSigners: detailedExecutionInfo.signers.filter(
                  (signer) =>
                    !detailedExecutionInfo.confirmations.some((confirmation) =>
                      sameAddress(confirmation.signer.value, signer.value),
                    ),
                ),
              }
            : undefined

        return {
          type: TransactionListItemType.TRANSACTION as const,
          conflictType: ConflictType.NONE,
          transaction: {
            id: details.txId,
            timestamp:
              details.detailedExecutionInfo && 'submittedAt' in details.detailedExecutionInfo
                ? details.detailedExecutionInfo.submittedAt
                : Date.now(),
            txStatus: details.txStatus,
            txInfo: details.txInfo,
            executionInfo,
            txHash: details.txHash ?? null,
          },
        }
      }),
    ],
  }
}

const buildTxInfo = (safeAddress: string, tx: SafeTransaction): TransactionDetails['txInfo'] => {
  const isNativeTransfer = !tx.data.data || tx.data.data === '0x'

  if (isNativeTransfer) {
    return {
      type: TransactionInfoType.TRANSFER,
      sender: toAddressEx(safeAddress),
      recipient: toAddressEx(tx.data.to),
      direction: TransferDirection.OUTGOING,
      transferInfo: {
        type: TransactionTokenType.NATIVE_COIN,
        value: tx.data.value,
      },
    }
  }

  return {
    type: TransactionInfoType.CUSTOM,
    to: toAddressEx(tx.data.to),
    dataSize: Math.max(0, (tx.data.data.length - 2) / 2).toString(),
    value: tx.data.value,
    isCancellation: false,
  }
}

export const saveLocalProposedTx = async ({
  chainId,
  safeAddress,
  sender,
  tx,
  safeTxHash,
}: {
  chainId: string
  safeAddress: string
  sender: string
  tx: SafeTransaction
  safeTxHash: string
}): Promise<TransactionDetails> => {
  const txId = getTxId(safeAddress, safeTxHash)
  const existing = getLocalProposedTx(txId)
  const safeInfo = await fetchSafeInfo(chainId, safeAddress)
  const submittedAt =
    existing?.detailedExecutionInfo && 'submittedAt' in existing.detailedExecutionInfo
      ? existing.detailedExecutionInfo.submittedAt
      : Date.now()

  const existingConfirmations = existing ? getConfirmations(existing) : []

  const signedConfirmations = Array.from(tx.signatures.values()).map((signature) => ({
    signer: toAddressEx(signature.signer),
    signature: signature.data,
    submittedAt: Date.now(),
  }))

  const confirmations = [
    ...existingConfirmations.filter(
      (confirmation) =>
        !signedConfirmations.some((signed) => sameAddress(signed.signer.value, confirmation.signer.value)),
    ),
    ...signedConfirmations,
  ]

  const threshold = safeInfo.threshold
  const txStatus =
    confirmations.length >= threshold ? TransactionStatus.AWAITING_EXECUTION : TransactionStatus.AWAITING_CONFIRMATIONS

  const details: TransactionDetails = {
    safeAddress: checksumAddress(safeAddress),
    txId,
    txStatus,
    txInfo: buildTxInfo(safeAddress, tx),
    txData: {
      hexData: tx.data.data || '0x',
      to: toAddressEx(tx.data.to),
      value: tx.data.value,
      operation: tx.data.operation as unknown as Operation,
      trustedDelegateCallTarget: false,
      addressInfoIndex: {},
    },
    detailedExecutionInfo: {
      type: DetailedExecutionInfoType.MULTISIG,
      submittedAt,
      nonce: tx.data.nonce,
      safeTxGas: String(tx.data.safeTxGas ?? '0'),
      baseGas: String(tx.data.baseGas ?? '0'),
      gasPrice: String(tx.data.gasPrice ?? '0'),
      gasToken: tx.data.gasToken || ZERO_ADDRESS,
      refundReceiver: toAddressEx(tx.data.refundReceiver || ZERO_ADDRESS),
      safeTxHash,
      signers: safeInfo.owners.map((owner) => toAddressEx(owner.value)),
      confirmationsRequired: threshold,
      confirmations,
      rejectors: [],
      trusted: true,
      proposer: toAddressEx(sender),
    },
  }

  const queue = readQueue()
  queue[txId] = { chainId, details }
  writeQueue(queue)

  return details
}
